import { useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, BillingBadge, useList, formatDate, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { useChannels } from "@/hooks/useChannels";
import { PaperPlaneTilt as Send, DownloadSimple, CalendarBlank, ChatCircleText } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { extractPreviewText, MessageContent } from "./Conversations";

const RANGE_OPTIONS = [
  { value: "today", label: "Hari ini" },
  { value: "7d", label: "7 hari" },
  { value: "30d", label: "30 hari" },
  { value: "90d", label: "90 hari" },
  { value: "all", label: "Semua waktu" },
  { value: "custom", label: "Rentang khusus" },
];

export default function Messages() {
  const channels = useChannels();
  const [range, setRange] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  // Params effectively applied to list + stats
  const effectiveDate = range === "custom"
    ? { date_from: dateFrom ? new Date(dateFrom).toISOString() : "", date_to: dateTo ? new Date(dateTo + "T23:59:59").toISOString() : "" }
    : { range };

  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters } = useList("/messages", effectiveDate);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);

  const channelName = (id) => channels.find((c) => c.id === id)?.name || "-";

  const fetchStats = useCallback(async () => {
    const params = { ...effectiveDate, ...filters };
    Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
    try {
      const res = await api.get("/messages/stats", params);
      setStats(res.data);
    } catch (e) { setStats(null); }
  }, [range, dateFrom, dateTo, filters.channel_id, filters.message_type]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (q) params.set("q", q);
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      Object.entries(effectiveDate).forEach(([k, v]) => v && params.set(k, v));
      const token = localStorage.getItem("serabi_token");
      const res = await fetch(`${API}/messages/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `messages-${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Gagal mengekspor pesan.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Message"
        subtitle="Tracking pesan keluar / outbox internal Anda."
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={exporting}
                className="inline-flex items-center gap-2 h-[42px] px-5 rounded-[4px] bg-[#FFDA1A] hover:bg-[#F5CC00] text-[#111111] font-semibold text-sm transition-colors disabled:opacity-60"
                data-testid="message-export-btn"
              >
                <DownloadSimple className="w-4 h-4" weight="bold" />
                {exporting ? "Mengekspor..." : "Export"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onSelect={() => handleExport("csv")} data-testid="message-export-csv">CSV (.csv)</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => handleExport("xlsx")} data-testid="message-export-xlsx">Excel (.xlsx)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* ===== Stats Overview ===== */}
      <div className="bg-white rounded-lg p-5 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2">
            <CalendarBlank className="w-4 h-4 text-[#767676]" weight="regular" />
            <span className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#767676]">Statistik Periode</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-40 bg-white h-9" data-testid="stats-range-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {range === "custom" && (
              <>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9 bg-white" data-testid="stats-date-from" />
                <span className="text-sm text-[#767676]">—</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9 bg-white" data-testid="stats-date-to" />
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCell label="Total" value={stats?.total ?? "-"} accent="text-[#111111]" testid="stat-total" />
          <StatCell label="Terkirim" value={stats?.by_status?.sent ?? "-"} accent="text-[#003399]" testid="stat-sent" />
          <StatCell label="Diterima" value={stats?.by_status?.delivered ?? "-"} accent="text-[#0A8A00]" testid="stat-delivered" />
          <StatCell label="Dibaca" value={stats?.by_status?.read ?? "-"} accent="text-[#003399]" testid="stat-read" />
          <StatCell label="Gagal" value={stats?.by_status?.failed ?? "-"} accent="text-[#CC0008]" testid="stat-failed" />
          <StatCell label="Berbayar" value={stats ? `${stats.billing?.paid ?? 0} · ${stats.billing?.paid_ratio ?? 0}%` : "-"} accent="text-[#111111]" testid="stat-paid" />
        </div>
      </div>

      {/* ===== Filters ===== */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari kontak, nomor, template..." testid="message-search" />
        <Select value={filters.channel_id || "all"} onValueChange={(v) => setFilters({ ...filters, channel_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-44 bg-white" data-testid="message-channel-filter"><SelectValue placeholder="Semua channel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua channel</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="message-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua status</SelectItem>
            {["pending", "sent", "delivered", "read", "failed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.message_type || "all"} onValueChange={(v) => setFilters({ ...filters, message_type: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36 bg-white" data-testid="message-type-filter"><SelectValue placeholder="Tipe" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua tipe</SelectItem>
            {["text", "template", "image", "document"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={Send} title="Belum ada pesan keluar" description="Pesan outbox internal yang Anda buat di Conversation akan muncul di sini." />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Penerima</th>
                <th className={`${thClass} hidden md:table-cell`}>Channel</th>
                <th className={thClass}>Tipe</th>
                <th className={thClass}>Biaya</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} hidden md:table-cell`}>Waktu</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id} onClick={() => setSelected(m)} className={trClass} data-testid={`message-row-${m.id}`}>
                  <td className={tdFirstClass}>
                    <p className="font-medium text-[#111111]">{m.contact_name || m.to_number}</p>
                    <p className="text-xs text-[#767676] truncate max-w-[220px]">{extractPreviewText(m) || `[${m.message_type}]`}</p>
                  </td>
                  <td className={`${tdClass} hidden md:table-cell`}>{channelName(m.channel_id)}</td>
                  <td className={`${tdClass} hidden lg:table-cell`}>{m.message_type}</td>
                  <td className={tdClass}><BillingBadge billing={m.billing} /></td>
                  <td className={tdClass}><StatusBadge status={m.status} /></td>
                  <td className={`${tdClass} hidden md:table-cell text-xs text-[#767676]`}>{formatDate(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={!!selected} onClose={() => setSelected(null)} title="Detail Pesan" description="Record outbox internal" testid="message-drawer">
        {selected && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={selected.status} />
              <BillingBadge billing={selected.billing} />
              <span className="text-sm text-neutral-500">{formatDate(selected.created_at)}</span>
            </div>

            {/* ===== Body preview (WhatsApp-like) ===== */}
            <div data-testid="message-drawer-body-preview">
              <div className="flex items-center gap-2 mb-2">
                <ChatCircleText className="w-4 h-4 text-[#767676]" weight="regular" />
                <span className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#767676]">Isi Pesan</span>
              </div>
              <div className="rounded-lg bg-[#FFF3C2] p-4 border-l-4 border-[#FFDA1A]">
                {selected.template_name && (
                  <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#003399] mb-2">
                    Template · {selected.template_name}
                  </p>
                )}
                <MessageContent message_type={selected.message_type} display={selected.display} />
                <p className="text-[10px] text-neutral-400 mt-2">{formatDate(selected.created_at)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Dari" value={selected.from_number} />
              <Field label="Ke" value={selected.to_number} />
              <Field label="Tipe" value={selected.message_type} />
              <Field label="Channel" value={channelName(selected.channel_id)} />
              <Field label="Arah" value={selected.direction} />
              <Field label="Kontak" value={selected.contact_name} />
            </div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}

const StatCell = ({ label, value, accent, testid }) => (
  <div className="border-l-4 border-[#F0F0F0] pl-3 py-1" data-testid={testid}>
    <p className="text-[10px] tracking-[0.08em] uppercase font-semibold text-[#767676] mb-1">{label}</p>
    <p className={`font-heading text-2xl font-bold leading-none ${accent}`}>{value}</p>
  </div>
);

const Field = ({ label, value }) => (
  <div><p className="text-xs text-neutral-500 mb-1">{label}</p><p className="text-sm font-medium text-neutral-900 break-words">{value || "-"}</p></div>
);
